import React from 'react';
import { multiSelectToggle as multiSelectToggleAction } from '@engage/filters/redux/applied';
import { useReduxAction } from '@engage/redux';

type Context = {
  isOpenMap: Record<string, boolean>;
  setIsOpenMap: React.Dispatch<React.SetStateAction<{}>>;
};

const FilterContext = React.createContext<Context>({
  isOpenMap: {},
  setIsOpenMap: (m: Record<string, boolean>) => null,
});

export const FilterProvider: React.FC<
  React.PropsWithChildren<{ isFlyoutOpen: boolean }>
> = ({ children, isFlyoutOpen }) => {
  const [isOpenMap, setIsOpenMap] = React.useState({});
  const [oldFilterParams, setOldFilterParams] = React.useState('');
  const multiSelectToggle = useReduxAction(multiSelectToggleAction);

  React.useEffect(() => {
    setOldFilterParams(location.search);
  }, []);

  React.useEffect(() => {
    const newFilterParams = location.search;
    if (newFilterParams !== oldFilterParams && !isFlyoutOpen) {
      multiSelectToggle();
      setOldFilterParams(newFilterParams);
    }
  }, [isFlyoutOpen]);

  const setIsOpenMapWithAnalytics = (mapName: string) => {
    const newFilterParams = location.search;
    if (newFilterParams !== oldFilterParams) {
      multiSelectToggle();
    }
    setOldFilterParams(newFilterParams);
    setIsOpenMap(mapName);
  };

  return (
    <FilterContext.Provider
      value={{
        isOpenMap,
        setIsOpenMap: setIsOpenMapWithAnalytics,
      }}
    >
      {children}
    </FilterContext.Provider>
  );
};

export const useFilterContext = (title: string) => {
  const { isOpenMap, setIsOpenMap } = React.useContext(FilterContext);
  const defaultIsOpenMap = Object.keys(isOpenMap).reduce(
    (acc, t) => ({ ...acc, [t]: false }),
    {},
  );

  const setIsOpen = (name: string) => (isOpen: boolean) => {
    if (isOpenMap[name]) {
      setIsOpenMap(defaultIsOpenMap);
    } else {
      setIsOpenMap({ ...defaultIsOpenMap, [name]: true });
    }
  };

  return [isOpenMap[title], setIsOpen(title)] as const;
};
